import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  getCachedUser,
  getToken,
  setCachedUser,
  setToken
} from "./api";
import { Shell } from "./components/Shell";
import { LoginPage } from "./pages/LoginPage";
import { SetupPage } from "./pages/SetupPage";
import { WhmHomePage } from "./pages/WhmHomePage";
import { CpanelHomePage } from "./pages/CpanelHomePage";
import { ServerConfigPage } from "./pages/ServerConfigPage";
import { AccountsPage } from "./pages/AccountsPage";
import type { WhmSectionId } from "./whmTools";
import { PackagesPage } from "./pages/PackagesPage";
import { HostPlansPage } from "./pages/HostPlansPage";
import { AuditPage } from "./pages/AuditPage";
import { SecurityPage } from "./pages/SecurityPage";
import { TokensPage } from "./pages/TokensPage";
import { SitesPage } from "./pages/SitesPage";
import { AutomaticSslPage } from "./pages/AutomaticSslPage";
import { FilesPage } from "./pages/FilesPage";
import { FtpPage } from "./pages/FtpPage";
import { DatabasesPage } from "./pages/DatabasesPage";
import { DnsPage } from "./pages/DnsPage";
import { MailPage } from "./pages/MailPage";
import { CronPage } from "./pages/CronPage";
import { LogsPage } from "./pages/LogsPage";
import { MetricsPage } from "./pages/MetricsPage";
import { BackupsPage } from "./pages/BackupsPage";
import { AppsPage } from "./pages/AppsPage";
import { AdminerPage } from "./pages/AdminerPage";
import { ResellersPage } from "./pages/ResellersPage";
import { BrandingPage } from "./pages/BrandingPage";
import { MigrationsPage } from "./pages/MigrationsPage";
import { ImportPage } from "./pages/ImportPage";
import { PolishPage } from "./pages/PolishPage";
import {
  AdvancedPage,
  advancedTabFromNav,
  type AdvancedTab
} from "./pages/AdvancedPage";
import { initAppearance } from "./i18n";
import type {
  ApiToken,
  AuditEntry,
  BrandingSettings,
  Health,
  LocalLicenseStatus,
  NavKey,
  HostPlan,
  PackagePlan,
  PanelUser,
  ResellerProfile,
  ServerNode,
  SessionInfo,
  Site
} from "./types";
import {
  isRouteAllowed,
  parseLocationRoute,
  routeStateFromNav,
  writeLocationRoute,
  type RouteState
} from "./lib/routes";

function resellerFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("reseller");
  } catch {
    return null;
  }
}

/** Initial nav from URL so first paint matches deep link (before effects). */
function initialRouteFromUrl(): RouteState {
  if (typeof window === "undefined") {
    return { nav: "overview", toolId: null, act: "home" };
  }
  return parseLocationRoute();
}

initAppearance();

export default function App() {
  const [token, setTok] = useState<string | null>(() => getToken());
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  // Hydrate from cache for zero-wait shell paint (no "Booting RealPanel" screen)
  const [user, setUser] = useState<PanelUser | null>(() =>
    getToken() ? getCachedUser<PanelUser>() : null
  );
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [os, setOs] = useState<Record<string, string> | null>(null);
  const [agent, setAgent] = useState<Record<string, unknown> | null>(null);
  const [meta, setMeta] = useState<{ phase?: number; version?: string } | null>(
    null
  );
  const [accounts, setAccounts] = useState<PanelUser[]>([]);
  /** Owner Packages (license seats) — staff only */
  const [packages, setPackages] = useState<PackagePlan[]>([]);
  /** Hosting Plans (RAM/CPU/disk) — User/Reseller create these */
  const [hostPlans, setHostPlans] = useState<HostPlan[]>([]);
  const [resellers, setResellers] = useState<ResellerProfile[]>([]);
  const [servers, setServers] = useState<ServerNode[]>([]);
  const [resellerMe, setResellerMe] = useState<ResellerProfile | null>(null);
  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [databasesCount, setDatabasesCount] = useState(0);
  const [loadAvg, setLoadAvg] = useState<string | null>(null);
  const [phpVersions, setPhpVersions] = useState<
    { version: string; available: boolean; default?: boolean }[]
  >([]);
  const [gatewayPort, setGatewayPort] = useState(8088);
  const [stats, setStats] = useState<{
    total: number;
    active: number;
    suspended: number;
    admins: number;
    provision?: { ready: number; failed: number; pending: number };
  } | null>(null);
  const [error, setError] = useState("");
  const [needsSetup, setNeedsSetup] = useState(false);
  /** admin = WHM port · user = cPanel port (Webuzo dual-port model) */
  const [panelKind, setPanelKind] = useState<"admin" | "user">("admin");
  const [adminPort, setAdminPort] = useState(7001);
  const [userPort, setUserPort] = useState(7002);
  const bootRoute = initialRouteFromUrl();
  const [nav, setNav] = useState<NavKey>(bootRoute.nav);
  const [licenseStatus, setLicenseStatus] = useState<LocalLicenseStatus | null>(
    null
  );
  const [whmSection, setWhmSection] = useState<WhmSectionId | "all">("all");
  const [whmSearch, setWhmSearch] = useState("");
  const [openCreateAccount, setOpenCreateAccount] = useState(
    () => Boolean(bootRoute.createAccount)
  );
  const [serverConfigTab, setServerConfigTab] = useState<
    "status" | "basic" | "tweaks" | "php" | "metrics" | "panel"
  >(() => bootRoute.serverConfigTab || "panel");
  const [advancedTab, setAdvancedTab] = useState<AdvancedTab>(
    () => bootRoute.advancedTab || "overview"
  );
  const [userFeatures, setUserFeatures] = useState<Record<
    string,
    boolean
  > | null>(null);

  const isSuperAdmin = user?.role === "superadmin";
  const isStaff = user?.role === "superadmin" || user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const isWhm =
    (isStaff || isReseller) && !session?.impersonating;

  const [activeToolId, setActiveToolId] = useState<string | null>(
    () => bootRoute.toolId
  );
  const [ftpTab, setFtpTab] = useState<"manage" | "add" | "connections">(
    () => bootRoute.ftpTab || "manage"
  );
  /** Skip pushState when applying history / URL (popstate / initial). */
  const skipUrlWriteRef = useRef(false);

  const applyRouteState = useCallback(
    (state: RouteState, opts?: { writeUrl?: boolean; replace?: boolean }) => {
      const allowed = isRouteAllowed(state, {
        isStaff,
        isSuperAdmin,
        isReseller,
        isEndUser: !isStaff && !isReseller,
        impersonating: Boolean(session?.impersonating),
        userFeatures
      });
      const next = allowed
        ? state
        : ({ nav: "overview", toolId: null, act: "home" } as RouteState);

      setNav(next.nav);
      setActiveToolId(next.toolId);
      if (next.ftpTab) setFtpTab(next.ftpTab);
      else if (next.nav === "ftp") setFtpTab("manage");
      if (next.createAccount) setOpenCreateAccount(true);
      else if (next.nav !== "accounts") setOpenCreateAccount(false);
      if (next.serverConfigTab) setServerConfigTab(next.serverConfigTab);
      else if (next.nav === "php") setServerConfigTab("php");
      else if (next.nav === "metrics" && !next.serverConfigTab) {
        setServerConfigTab("status");
      }
      if (next.advancedTab) setAdvancedTab(next.advancedTab);
      else if (
        next.nav === "advanced" ||
        next.nav === "servers" ||
        next.nav === "isolation" ||
        next.nav === "webhooks" ||
        next.nav === "licenses"
      ) {
        setAdvancedTab(advancedTabFromNav(next.nav));
      }

      if (opts?.writeUrl !== false && !skipUrlWriteRef.current) {
        writeLocationRoute(next, opts?.replace ? "replace" : "push");
      }
    },
    [isStaff, isSuperAdmin, isReseller, session?.impersonating, userFeatures]
  );

  const goNav = (
    k: NavKey,
    opts?: {
      createAccount?: boolean;
      serverConfigTab?:
        | "status"
        | "basic"
        | "tweaks"
        | "php"
        | "metrics"
        | "panel";
      advancedTab?: AdvancedTab;
      toolId?: string;
      ftpTab?: "manage" | "add" | "connections";
    }
  ) => {
    // W5: block cPanel tools only when package feature is explicitly false
    const featureKey =
      k === "ftp"
        ? "files"
        : k === "ssl"
          ? "sites"
          : k === "cron"
            ? "cron"
            : k;
    if (
      userFeatures &&
      k !== "overview" &&
      userFeatures[featureKey] === false &&
      ((!isStaff && !isReseller) || session?.impersonating)
    ) {
      applyRouteState({ nav: "overview", toolId: null, act: "home" });
      return;
    }
    const state = routeStateFromNav(k, {
      toolId: opts?.toolId,
      createAccount: opts?.createAccount,
      serverConfigTab: opts?.serverConfigTab,
      advancedTab: opts?.advancedTab,
      ftpTab: opts?.ftpTab
    });
    applyRouteState(state);
  };

  // Browser back/forward → restore route from URL
  useEffect(() => {
    const onPop = () => {
      skipUrlWriteRef.current = true;
      try {
        applyRouteState(parseLocationRoute(), { writeUrl: false });
      } finally {
        skipUrlWriteRef.current = false;
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [applyRouteState]);

  // After auth/features load, re-apply deep link (permission-aware) + sync URL
  useEffect(() => {
    if (!token || !user) return;
    const fromUrl = parseLocationRoute();
    skipUrlWriteRef.current = true;
    try {
      applyRouteState(fromUrl, { writeUrl: false });
    } finally {
      skipUrlWriteRef.current = false;
    }
    // Ensure address bar has act= even if user landed without it
    writeLocationRoute(
      isRouteAllowed(fromUrl, {
        isStaff,
        isSuperAdmin,
        isReseller,
        isEndUser: !isStaff && !isReseller,
        impersonating: Boolean(session?.impersonating),
        userFeatures
      })
        ? fromUrl
        : { nav: "overview", toolId: null, act: "home" },
      "replace"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when identity/features change
  }, [token, user?.id, user?.role, userFeatures, session?.impersonating]);

  const applyAuth = (payload: {
    token: string;
    user: PanelUser;
  }) => {
    setToken(payload.token);
    setTok(payload.token);
    setUser(payload.user);
    setCachedUser(payload.user);
    setMfaToken(null);
  };

  const loadBranding = useCallback(async (resellerUsername?: string | null) => {
    try {
      const q = resellerUsername
        ? `?reseller=${encodeURIComponent(resellerUsername)}`
        : "";
      const res = await api<{ ok: boolean; branding: BrandingSettings }>(
        `/api/v1/branding${q}`
      );
      setBranding(res.branding);
    } catch {
      setBranding(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    // Soft fetch — never throw; keeps UI snappy when one call fails
    const soft = async <T,>(p: Promise<T>, fallback: T): Promise<T> => {
      try {
        return await p;
      } catch {
        return fallback;
      }
    };

    // 1) Critical path first (auth) — then fan-out everything else in parallel
    const h = await soft(api<Health>("/health"), null as unknown as Health);
    if (h) setHealth(h);
    if (!getToken()) return;

    const me = await api<{
      ok: boolean;
      user: PanelUser;
      session: SessionInfo;
    }>("/api/v1/auth/me");
    setUser(me.user);
    setCachedUser(me.user);
    setSession(me.session);

    const staff = me.user.role === "superadmin" || me.user.role === "admin";
    const reseller = me.user.role === "reseller";

    // 2) Parallel batch — previously sequential (~15 round-trips)
    const [
      osRes,
      agentRes,
      metaRes,
      licRes,
      featRes,
      planRes,
      pkgRes,
      accRes,
      siteRes,
      dbRes,
      metRes,
      phpRes,
      resellerMeRes,
      resellersRes,
      serversRes,
      webStatusRes,
      statsRes
    ] = await Promise.all([
      soft(
        api<{ ok: boolean; os?: Record<string, string> }>("/api/v1/system/os"),
        { ok: false as const, os: undefined }
      ),
      soft(
        api<{ ok: boolean; agent: Record<string, unknown> }>(
          "/api/v1/system/agent"
        ),
        { ok: false as const, agent: {} }
      ),
      soft(
        api<{ ok: boolean; phase: number; version: string }>(
          "/api/v1/system/meta"
        ),
        { ok: false as const, phase: 0, version: "" }
      ),
      soft(
        api<{ ok: boolean; status: LocalLicenseStatus }>(
          "/api/v1/license/status"
        ),
        { ok: false as const, status: null as unknown as LocalLicenseStatus }
      ),
      soft(
        api<{ ok: boolean; features: Record<string, boolean> }>(
          "/api/v1/me/features"
        ),
        { ok: false as const, features: {} }
      ),
      soft(api<{ ok: boolean; items: HostPlan[] }>("/api/v1/plans"), {
        ok: false as const,
        items: [] as HostPlan[]
      }),
      me.user.role === "superadmin"
        ? soft(api<{ ok: boolean; items: PackagePlan[] }>("/api/v1/packages"), {
            ok: false as const,
            items: [] as PackagePlan[]
          })
        : Promise.resolve({ ok: false as const, items: [] as PackagePlan[] }),
      soft(api<{ ok: boolean; items: PanelUser[] }>("/api/v1/accounts"), {
        ok: false as const,
        items: [] as PanelUser[]
      }),
      soft(api<{ ok: boolean; items: Site[] }>("/api/v1/sites"), {
        ok: false as const,
        items: [] as Site[]
      }),
      soft(api<{ ok: boolean; items: unknown[] }>("/api/v1/databases"), {
        ok: false as const,
        items: [] as unknown[]
      }),
      soft(
        api<{
          ok?: boolean;
          metrics?: {
            load?: { one: number; five: number; fifteen: number };
          };
        }>("/api/v1/metrics"),
        { ok: false as const, metrics: undefined }
      ),
      soft(
        api<{
          ok: boolean;
          items: { version: string; available: boolean; default?: boolean }[];
        }>("/api/v1/sites/php-versions"),
        {
          ok: false as const,
          items: [
            { version: "8.3", available: true, default: true },
            { version: "8.2", available: true },
            { version: "8.1", available: true }
          ]
        }
      ),
      reseller
        ? soft(
            api<{ ok: boolean; reseller: ResellerProfile }>(
              "/api/v1/resellers/me"
            ),
            { ok: false as const, reseller: null as unknown as ResellerProfile }
          )
        : Promise.resolve({
            ok: false as const,
            reseller: null as unknown as ResellerProfile
          }),
      staff
        ? soft(
            api<{ ok: boolean; items: ResellerProfile[] }>("/api/v1/resellers"),
            { ok: false as const, items: [] as ResellerProfile[] }
          )
        : Promise.resolve({
            ok: false as const,
            items: [] as ResellerProfile[]
          }),
      staff
        ? soft(api<{ ok: boolean; items: ServerNode[] }>("/api/v1/servers"), {
            ok: false as const,
            items: [] as ServerNode[]
          })
        : Promise.resolve({ ok: false as const, items: [] as ServerNode[] }),
      staff
        ? soft(
            api<{ ok: boolean; status: { gatewayPort?: number } }>(
              "/api/v1/sites/web-status"
            ),
            { ok: false as const, status: {} }
          )
        : Promise.resolve({ ok: false as const, status: {} }),
      staff
        ? soft(
            api<{
              ok: boolean;
              total: number;
              active: number;
              suspended: number;
              admins: number;
              provision?: { ready: number; failed: number; pending: number };
            }>("/api/v1/accounts/stats"),
            {
              ok: false as const,
              total: 0,
              active: 0,
              suspended: 0,
              admins: 0
            }
          )
        : Promise.resolve({
            ok: false as const,
            total: 0,
            active: 0,
            suspended: 0,
            admins: 0
          })
    ]);

    setOs(osRes.os || null);
    setAgent(agentRes.ok ? agentRes.agent : null);
    if (metaRes.ok) setMeta({ phase: metaRes.phase, version: metaRes.version });
    setLicenseStatus(licRes.status || null);
    setUserFeatures(featRes.features || null);
    setHostPlans(planRes.items || []);
    setPackages(me.user.role === "superadmin" ? pkgRes.items || [] : []);
    setAccounts(accRes.items || []);
    setSites(siteRes.items || []);
    setDatabasesCount((dbRes.items || []).length);
    setPhpVersions(phpRes.items || []);

    const load = metRes.metrics?.load;
    if (load) {
      setLoadAvg(
        `${Number(load.one).toFixed(2)}, ${Number(load.five).toFixed(2)}, ${Number(load.fifteen).toFixed(2)}`
      );
    } else {
      setLoadAvg(null);
    }

    if (reseller && resellerMeRes.reseller) {
      const rm = resellerMeRes.reseller;
      setResellerMe(rm);
      setBranding({
        productName: rm.productName,
        logoUrl: rm.logoUrl,
        supportUrl: rm.supportUrl,
        nameservers: rm.nameservers,
        tagline: rm.company
          ? `${rm.company} control panel`
          : "Managed hosting control panel",
        resellerUsername: rm.username || me.user.username
      });
    } else {
      setResellerMe(null);
    }

    if (staff) {
      setResellers(resellersRes.items || []);
      setServers(serversRes.items || []);
      const gp = (webStatusRes.status as { gatewayPort?: number } | undefined)
        ?.gatewayPort;
      if (gp) setGatewayPort(gp);
      if (statsRes.ok) {
        setStats({
          total: statsRes.total,
          active: statsRes.active,
          suspended: statsRes.suspended,
          admins: statsRes.admins,
          provision: statsRes.provision
        });
      } else {
        setStats(null);
      }
      // Audit + tokens are heavy and rarely needed on every navigation — load lazily in background
      void soft(
        api<{ ok: boolean; items: AuditEntry[]; total: number }>(
          "/api/v1/audit?limit=50"
        ),
        { ok: false as const, items: [] as AuditEntry[], total: 0 }
      ).then((a) => {
        setAudit(a.items);
        setAuditTotal(a.total);
      });
      void soft(api<{ ok: boolean; items: ApiToken[] }>("/api/v1/tokens"), {
        ok: false as const,
        items: [] as ApiToken[]
      }).then((t) => setTokens(t.items));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Detect admin vs user panel port (Webuzo dual-port model)
        const info = await api<{
          ok: boolean;
          panel: "admin" | "user";
          adminPort: number;
          userPort: number;
        }>("/api/v1/panel-info").catch(() => null);
        if (!cancelled && info) {
          setPanelKind(info.panel === "user" ? "user" : "admin");
          if (info.adminPort) setAdminPort(info.adminPort);
          if (info.userPort) setUserPort(info.userPort);
        }

        if (token) {
          // Instant shell via cache; refresh in background (no boot screen)
          setNeedsSetup(false);
          await refresh();
          if (cancelled) return;
        } else {
          // Login UI immediately — health / setup / branding load in parallel
          const [h, setup] = await Promise.all([
            api<Health>("/health").catch(() => null),
            api<{ ok: boolean; needsSetup: boolean }>("/api/v1/setup/status").catch(
              () => ({ ok: true, needsSetup: false })
            )
          ]);
          if (cancelled) return;
          if (h) setHealth(h);
          setNeedsSetup(Boolean(setup.needsSetup));
          void loadBranding(resellerFromUrl());
        }
      } catch (err) {
        if (cancelled) return;
        // Wrong panel for current session token
        const msg = err instanceof Error ? err.message : String(err);
        if (/WRONG_PANEL|admin port|user panel/i.test(msg)) {
          setError(msg);
        }
        setToken(null);
        setTok(null);
        setUser(null);
        setCachedUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refresh, loadBranding]);

  const onLogin = async (username: string, password: string) => {
    setError("");
    const res = await api<{
      ok: boolean;
      mfaRequired?: boolean;
      mfaToken?: string;
      token?: string;
      user?: PanelUser;
    }>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password })
    });
    if (res.mfaRequired && res.mfaToken) {
      setMfaToken(res.mfaToken);
      return;
    }
    if (res.token && res.user) {
      applyAuth({ token: res.token, user: res.user });
      await refresh();
    }
  };

  const onMfa = async (totpCode: string) => {
    setError("");
    if (!mfaToken) return;
    const res = await api<{
      ok: boolean;
      token: string;
      user: PanelUser;
    }>("/api/v1/auth/mfa", {
      method: "POST",
      body: JSON.stringify({ mfaToken, totpCode })
    });
    applyAuth(res);
    await refresh();
  };

  const onLogout = () => {
    setToken(null);
    setTok(null);
    setUser(null);
    setCachedUser(null);
    setSession(null);
    setMfaToken(null);
    setAccounts([]);
    setPackages([]);
    setNav("overview");
    setActiveToolId(null);
    writeLocationRoute({ nav: "overview", toolId: null, act: "home" }, "replace");
  };

  const onStopImpersonation = async () => {
    const res = await api<{ ok: boolean; token: string; user: PanelUser }>(
      "/api/v1/auth/stop-impersonation",
      { method: "POST", body: "{}" }
    );
    applyAuth(res);
    await refresh();
  };

  // phpMyAdmin SSO shell (Webuzo-style) — standalone, no panel chrome
  if (
    typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/phpmyadmin") ||
      window.location.pathname.startsWith("/adminer"))
  ) {
    return <AdminerPage />;
  }

  // Instant first paint — never block on a full-screen boot loader
  if (!token || !user) {
    if (needsSetup) {
      return (
        <>
          <div className="shell-bg" />
          <SetupPage
            health={health}
            error={error}
            onComplete={({ token: t, user: u, message }) => {
              applyAuth({ token: t, user: u });
              setNeedsSetup(false);
              if (message) setError("");
              void refresh();
            }}
          />
        </>
      );
    }
    return (
      <>
        <div className="shell-bg" />
        <LoginPage
          panel={panelKind}
          adminPort={adminPort}
          userPort={userPort}
          onLogin={async (u, p) => {
            try {
              await onLogin(u, p);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
          onMfa={async (code) => {
            try {
              await onMfa(code);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
          error={error}
          health={health}
          mfaPending={Boolean(mfaToken)}
          branding={branding}
        />
      </>
    );
  }

  return (
    <>
      <div className="shell-bg" />
      <Shell
        user={user}
        session={session}
        nav={nav}
        onNav={goNav}
        onLogout={onLogout}
        onStopImpersonation={() => void onStopImpersonation()}
        branding={branding}
        licenseStatus={licenseStatus}
        onOpenLicenses={
          isStaff ? () => setNav("licenses") : undefined
        }
        whmSection={whmSection}
        onWhmSection={setWhmSection}
        userFeatures={
          isWhm && !session?.impersonating ? null : userFeatures
        }
        os={os}
        meta={meta}
        loadAvg={loadAvg}
        whmSearch={whmSearch}
        onWhmSearch={setWhmSearch}
        activeToolId={activeToolId}
      >
        {nav === "overview" ? (
          isWhm ? (
            <WhmHomePage
              user={user}
              health={health}
              os={os}
              agent={agent}
              meta={meta}
              stats={stats}
              packages={[]}
              hostPlans={hostPlans}
              resellerProfile={isReseller ? resellerMe : null}
              accounts={accounts}
              sites={sites}
              sitesCount={sites.length}
              databasesCount={databasesCount}
              licenseStatus={licenseStatus}
              searchQuery={whmSearch}
              onSearchChange={setWhmSearch}
              onRefresh={refresh}
              onNav={goNav}
              onOpenLicenses={
                isStaff ? () => setNav("licenses") : undefined
              }
              initialSection={
                whmSection === "all" ? undefined : whmSection
              }
            />
          ) : (
            <CpanelHomePage
              user={user}
              features={userFeatures}
              packageName={user.packageName}
              sitesCount={
                sites.filter((s) => s.userId === user.id).length ||
                sites.length
              }
              databasesCount={
                // best-effort: same user scope when API returns all
                databasesCount
              }
              serverIp={licenseStatus?.serverIp || os?.hostname || null}
              searchQuery={whmSearch}
              onSearchChange={setWhmSearch}
              onNav={goNav}
              onRefresh={refresh}
            />
          )
        ) : null}

        {nav === "files" ? (
          <FilesPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "ftp" ? (
          <FtpPage
            accounts={accounts}
            currentUser={user}
            sites={sites}
            onRefreshAccounts={refresh}
            initialTab={ftpTab}
          />
        ) : null}

        {nav === "databases" ? (
          <DatabasesPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "dns" ? (
          <DnsPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "email" ? (
          <MailPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "cron" ? (
          <CronPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "logs" ? (
          <LogsPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "metrics" && isStaff && !session?.impersonating ? (
          <ServerConfigPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
            initialTab={
              serverConfigTab === "php" ? "status" : serverConfigTab
            }
          />
        ) : nav === "metrics" ? (
          <MetricsPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "backups" ? (
          <BackupsPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "apps" ? (
          <AppsPage
            accounts={accounts}
            sites={sites}
            currentUser={user}
            onRefreshAccounts={refresh}
            toolId={activeToolId}
            initialMode={
              activeToolId === "installed-apps" ||
              activeToolId === "update-apps"
                ? "installed"
                : activeToolId === "enduser-apps" ||
                    activeToolId === "website-apps"
                  ? "website"
                  : activeToolId === "apps-services"
                    ? "services"
                    : activeToolId || "install"
            }
          />
        ) : null}

        {nav === "php" && isStaff && !session?.impersonating ? (
          <ServerConfigPage
            accounts={accounts}
            currentUser={user}
            onRefreshAccounts={refresh}
            initialTab="php"
          />
        ) : null}

        {nav === "sites" ? (
          <SitesPage
            sites={sites}
            accounts={accounts}
            phpVersions={phpVersions}
            gatewayPort={gatewayPort}
            currentUser={user}
            initialMode={
              activeToolId === "add-domain" ? "add" : "list"
            }
            onRefresh={refresh}
            onCreate={async (data) => {
              const res = await api<{ ok: boolean; site?: Site }>(
                "/api/v1/sites",
                {
                  method: "POST",
                  body: JSON.stringify(data)
                }
              );
              await refresh();
              return res.site;
            }}
            onEnable={async (id) => {
              await api(`/api/v1/sites/${id}/enable`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onDisable={async (id) => {
              await api(`/api/v1/sites/${id}/disable`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onDelete={async (id) => {
              await api(`/api/v1/sites/${id}`, { method: "DELETE" });
              await refresh();
            }}
            onSetPhp={async (id, phpVersion) => {
              await api(`/api/v1/sites/${id}/php-version`, {
                method: "POST",
                body: JSON.stringify({ phpVersion })
              });
              await refresh();
            }}
            onIssueSsl={async (id) => {
              const res = await api<{
                ok: boolean;
                result?: { note?: string };
              }>(`/api/v1/sites/${id}/ssl/issue`, {
                method: "POST",
                body: JSON.stringify({ forceHttps: true })
              });
              return res.result?.note;
            }}
            onForceHttps={async (id, force) => {
              await api(`/api/v1/sites/${id}/ssl/force-https`, {
                method: "POST",
                body: JSON.stringify({ forceHttps: force })
              });
            }}
            onCustomSsl={async (id, data) => {
              await api(`/api/v1/sites/${id}/ssl/custom`, {
                method: "POST",
                body: JSON.stringify({ ...data, forceHttps: true })
              });
            }}
            onRenewSsl={async (dryRun) => {
              await api("/api/v1/ssl/renew", {
                method: "POST",
                body: JSON.stringify({ dryRun: Boolean(dryRun) })
              });
            }}
          />
        ) : null}

        {nav === "ssl" ? (
          <AutomaticSslPage
            currentUser={user}
            isStaff={Boolean(isStaff && !session?.impersonating)}
          />
        ) : null}

        {nav === "accounts" ? (
          <AccountsPage
            accounts={accounts}
            plans={hostPlans}
            servers={servers}
            currentUser={user}
            openCreate={openCreateAccount}
            onOpenCreateConsumed={() => setOpenCreateAccount(false)}
            onRefresh={refresh}
            onCreate={async (data) => {
              const res = await api<{
                ok: boolean;
                provisionError?: string;
                setupError?: string;
                domainError?: string;
              }>("/api/v1/accounts", {
                method: "POST",
                body: JSON.stringify(data)
              });
              await refresh();
              return {
                setupError: res.setupError || res.provisionError,
                domainError: res.domainError
              };
            }}
            onUpdate={async (id, data) => {
              await api(`/api/v1/accounts/${id}`, {
                method: "PATCH",
                body: JSON.stringify(data)
              });
              await refresh();
            }}
            onResetPassword={async (id, password) => {
              await api(`/api/v1/accounts/${id}/reset-password`, {
                method: "POST",
                body: JSON.stringify({ password })
              });
              await refresh();
            }}
            onAssignServer={async (id, serverId) => {
              await api(`/api/v1/accounts/${id}/server`, {
                method: "POST",
                body: JSON.stringify({ serverId })
              });
              await refresh();
            }}
            onMassAction={async (action, ids) => {
              await api("/api/v1/accounts/mass", {
                method: "POST",
                body: JSON.stringify({ action, ids })
              });
              await refresh();
            }}
            onSuspend={async (id) => {
              await api(`/api/v1/accounts/${id}/suspend`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onUnsuspend={async (id) => {
              await api(`/api/v1/accounts/${id}/unsuspend`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onDelete={async (id) => {
              await api(`/api/v1/accounts/${id}`, { method: "DELETE" });
              await refresh();
            }}
            onImpersonate={async (id) => {
              const res = await api<{
                ok: boolean;
                token: string;
                user: PanelUser;
              }>("/api/v1/auth/impersonate", {
                method: "POST",
                body: JSON.stringify({ userId: id })
              });
              applyAuth(res);
              setNav("overview");
              await refresh();
            }}
            onProvision={async (id) => {
              await api(`/api/v1/accounts/${id}/provision`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onRefreshUsage={async (id) => {
              await api(`/api/v1/accounts/${id}/refresh-usage`, {
                method: "POST",
                body: "{}"
              });
              await refresh();
            }}
            onLoadDetail={async (id) => {
              const res = await api<{
                ok: boolean;
                account: PanelUser;
                sshKeys: import("./types").SshKey[];
                jobs: import("./types").ProvisionJob[];
              }>(`/api/v1/accounts/${id}`);
              return {
                account: res.account,
                sshKeys: res.sshKeys || [],
                jobs: res.jobs || []
              };
            }}
            onAddSshKey={async (id, name, publicKey) => {
              await api(`/api/v1/accounts/${id}/ssh-keys`, {
                method: "POST",
                body: JSON.stringify({ name, publicKey })
              });
            }}
            onRemoveSshKey={async (id, keyId) => {
              await api(`/api/v1/accounts/${id}/ssh-keys/${keyId}`, {
                method: "DELETE"
              });
            }}
            onSetSshAccess={async (id, sshAccess, sftpOnly) => {
              await api(`/api/v1/accounts/${id}/ssh`, {
                method: "PATCH",
                body: JSON.stringify({ sshAccess, sftpOnly })
              });
              await refresh();
            }}
          />
        ) : null}

        {/* Owner Packages (license seats) — staff only */}
        {nav === "packages" && isSuperAdmin && !session?.impersonating ? (
          <PackagesPage
            packages={packages}
            isStaff={true}
            onRefresh={refresh}
            onCreate={async (data) => {
              await api("/api/v1/packages", {
                method: "POST",
                body: JSON.stringify(data)
              });
              await refresh();
            }}
            onUpdate={async (id, data) => {
              await api(`/api/v1/packages/${id}`, {
                method: "PATCH",
                body: JSON.stringify(data)
              });
              await refresh();
            }}
            onDelete={async (id) => {
              await api(`/api/v1/packages/${id}`, { method: "DELETE" });
              await refresh();
            }}
          />
        ) : null}

        {/* Hosting Plans (RAM/CPU/disk) — staff + reseller */}
        {nav === "plans" && (isStaff || isReseller) ? (
          <HostPlansPage
            plans={hostPlans}
            currentUser={user}
            onRefresh={refresh}
            mode={
              activeToolId === "vps-plans"
                ? "vps"
                : activeToolId === "reseller-plans"
                  ? "reseller"
                  : "hosting"
            }
          />
        ) : null}

        {nav === "resellers" && isStaff ? (
          <ResellersPage
            resellers={resellers}
            packages={packages}
            onRefresh={refresh}
            onCreate={async (data) => {
              const nameservers =
                typeof data.nameservers === "string"
                  ? data.nameservers
                      .split(/[\n,]+/)
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : data.nameservers;
              await api("/api/v1/resellers", {
                method: "POST",
                body: JSON.stringify({ ...data, nameservers })
              });
              await refresh();
            }}
            onUpdate={async (id, data) => {
              await api(`/api/v1/resellers/${id}`, {
                method: "PATCH",
                body: JSON.stringify(data)
              });
              await refresh();
            }}
          />
        ) : null}

        {(nav === "advanced" ||
          nav === "servers" ||
          nav === "isolation" ||
          nav === "webhooks" ||
          nav === "licenses") &&
        isStaff &&
        !session?.impersonating ? (
          <AdvancedPage
            accounts={accounts}
            servers={servers}
            onRefresh={refresh}
            initialTab={
              nav === "advanced"
                ? advancedTab
                : advancedTabFromNav(nav)
            }
            serversHandlers={{
              onCreate: async (data) => {
                await api("/api/v1/servers", {
                  method: "POST",
                  body: JSON.stringify(data)
                });
                await refresh();
              },
              onUpdate: async (id, data) => {
                await api(`/api/v1/servers/${id}`, {
                  method: "PATCH",
                  body: JSON.stringify(data)
                });
                await refresh();
              },
              onDelete: async (id) => {
                await api(`/api/v1/servers/${id}`, { method: "DELETE" });
                await refresh();
              },
              onProbe: async (id) => {
                await api(`/api/v1/servers/${id}/probe`, {
                  method: "POST",
                  body: "{}"
                });
                await refresh();
              },
              onProbeAll: async () => {
                await api("/api/v1/servers/probe-all", {
                  method: "POST",
                  body: "{}"
                });
                await refresh();
              }
            }}
          />
        ) : null}

        {nav === "migrations" ? (
          <MigrationsPage
            accounts={accounts}
            servers={servers}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "import" && isStaff && !session?.impersonating ? (
          <ImportPage
            mode={
              activeToolId === "import-webuzo" ||
              activeToolId === "webuzo"
                ? "webuzo"
                : "cpanel"
            }
            accounts={accounts}
            servers={servers}
            currentUser={user}
            onRefreshAccounts={refresh}
          />
        ) : null}

        {nav === "polish" ? (
          <PolishPage isStaff={Boolean(isStaff && !session?.impersonating)} />
        ) : null}

        {nav === "branding" && isReseller ? (
          <BrandingPage
            profile={resellerMe}
            onRefresh={refresh}
            onSave={async (data) => {
              await api("/api/v1/resellers/me/branding", {
                method: "PATCH",
                body: JSON.stringify({
                  productName: data.productName,
                  logoUrl: data.logoUrl || null,
                  supportUrl: data.supportUrl || null,
                  company: data.company,
                  nameservers: data.nameservers
                })
              });
              await refresh();
            }}
          />
        ) : null}

        {nav === "audit" && isStaff ? (
          <AuditPage items={audit} total={auditTotal} onRefresh={refresh} />
        ) : null}

        {nav === "tokens" && isStaff ? (
          <TokensPage
            tokens={tokens}
            onRefresh={refresh}
            onCreate={async (name) => {
              const res = await api<{
                ok: boolean;
                secret: string;
              }>("/api/v1/tokens", {
                method: "POST",
                body: JSON.stringify({ name })
              });
              await refresh();
              return res.secret;
            }}
            onRevoke={async (id) => {
              await api(`/api/v1/tokens/${id}`, { method: "DELETE" });
              await refresh();
            }}
          />
        ) : null}

        {nav === "security" ? (
          <SecurityPage
            user={user}
            onRefresh={refresh}
            onChangePassword={async (currentPassword, newPassword) => {
              await api("/api/v1/auth/change-password", {
                method: "POST",
                body: JSON.stringify({ currentPassword, newPassword })
              });
            }}
            onSetup2fa={async () => {
              const res = await api<{
                ok: boolean;
                secret: string;
                otpauthUrl: string;
              }>("/api/v1/auth/2fa/setup", {
                method: "POST",
                body: "{}"
              });
              return { secret: res.secret, otpauthUrl: res.otpauthUrl };
            }}
            onEnable2fa={async (code) => {
              await api("/api/v1/auth/2fa/enable", {
                method: "POST",
                body: JSON.stringify({ code })
              });
            }}
            onDisable2fa={async (password, code) => {
              await api("/api/v1/auth/2fa/disable", {
                method: "POST",
                body: JSON.stringify({ password, code })
              });
            }}
          />
        ) : null}
      </Shell>
    </>
  );
}
